import { z } from 'zod';

/**
 * The day a calendar week begins on, as a market convention.
 *
 * ## Why this is a published SETTING and not a derivation
 *
 * `Intl.Locale.prototype.getWeekInfo` answers this from CLDR — and CLDR answers
 * a question about the *locale*, not about the deployment. A brand running in
 * Riyadh with English-speaking staff would be told Monday, and an `ar` with no
 * region is not a country at all; `ar-MA` genuinely starts on Monday while
 * `ar-SA` starts on Sunday. The week start is a fact about **where the platform
 * operates**, which is exactly what `brand.*` holds (U16 already names date and
 * time formats there), so it is resolved once by the api and published to every
 * client rather than each of them guessing from its own locale.
 *
 * Three values rather than all seven, because the rest are not conventions any
 * market this platform serves uses, and an operator picking Wednesday from a
 * seven-item select is a mis-click nobody would notice.
 *
 * **The order is the display order, not the week's.** `saturday` first because
 * it is the Gulf convention and Arabic is the default experience (U8).
 */
export const WEEK_START_DAYS = ['saturday', 'sunday', 'monday'] as const;

export const WeekStartDaySchema = z.enum(WEEK_START_DAYS);

export type WeekStartDay = (typeof WEEK_START_DAYS)[number];

/**
 * The same days as `Date.prototype.getDay()` indexes them — Sunday is 0.
 *
 * A grid has to turn a name into an offset, and doing that with an
 * `indexOf(WEEK_START_DAYS)` would silently re-map every column the day a fourth
 * value is added in a different position. This map is the one place the two
 * numbering systems meet.
 */
export const WEEK_START_DAY_INDEX: Readonly<Record<WeekStartDay, number>> = {
  saturday: 6,
  sunday: 0,
  monday: 1,
};
